import { Card, CardContent, CircularProgress, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { MdWarningAmber } from "react-icons/md";
import { useSlaBreaches } from "hooks/useTicketsAttentionQueue";
import { SlaBreachItem } from "interfaces/entities/ticketStats";
import moment from "moment";

const PRIORITY_STYLE: Record<string, { label: string; bg: string; color: string }> = {
  HIGH: { label: "Высокий", bg: "#FEE2E2", color: "#B91C1C" },
  MEDIUM: { label: "Средний", bg: "#FEF3C7", color: "#92400E" },
  LOW: { label: "Низкий", bg: "#E0F2FE", color: "#075985" },
};

const STATUS_LABEL: Record<string, string> = {
  NEW: "Новый",
  IN_PROGRESS: "В работе",
  WAITING_FOR_RESPONSE: "Ждёт ответа",
};

const formatHours = (hours: number): string => {
  if (hours < 1) return `${Math.round(hours * 60)} мин`;
  if (hours < 24) return `${hours.toFixed(1)} ч`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours - days * 24);
  return rem > 0 ? `${days}д ${rem}ч` : `${days}д`;
};

const customerName = (c: SlaBreachItem["customer"]): string => {
  if (!c) return "Без клиента";
  const parts = [c.firstName, c.lastName].filter(Boolean);
  return parts.join(" ") || "Клиент";
};

const PriorityChip = ({ priority }: { priority: string | null }) => {
  const key = priority || "MEDIUM";
  const style = PRIORITY_STYLE[key] || PRIORITY_STYLE.MEDIUM;
  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 6px",
        borderRadius: 4,
        letterSpacing: 0.4,
      }}
    >
      {style.label}
    </span>
  );
};

const PriorityBreakdown = ({
  byPriority,
  sla,
}: {
  byPriority: { HIGH: number; MEDIUM: number; LOW: number };
  sla: { HIGH: number; MEDIUM: number; LOW: number };
}) => {
  const items: Array<{ key: keyof typeof byPriority; count: number; sla: number }> = [
    { key: "HIGH", count: byPriority.HIGH, sla: sla.HIGH },
    { key: "MEDIUM", count: byPriority.MEDIUM, sla: sla.MEDIUM },
    { key: "LOW", count: byPriority.LOW, sla: sla.LOW },
  ];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {items.map((it) => {
        const style = PRIORITY_STYLE[it.key];
        return (
          <Tooltip key={it.key} title={`SLA на решение: ${it.sla}ч`} arrow>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                background: style.bg,
                color: style.color,
                padding: "6px 10px",
                borderRadius: 6,
                minWidth: 80,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4 }}>
                {style.label}
              </span>
              <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{it.count}</span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

const BreachRow = ({ ticket }: { ticket: SlaBreachItem }) => {
  return (
    <Link
      to={`/admin/tickets/${ticket.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 12,
        alignItems: "center",
        padding: "10px 12px",
        borderBottom: "1px solid #F1F5F9",
        textDecoration: "none",
        color: "inherit",
        transition: "background-color 120ms",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FAFAFA")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <PriorityChip priority={ticket.priority} />

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={ticket.subject}
        >
          #{ticket.id} · {ticket.subject || "Без темы"}
        </div>
        <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
          {STATUS_LABEL[ticket.status] || ticket.status}
          {" · "}
          {customerName(ticket.customer)}
          {ticket.branch?.name ? ` · ${ticket.branch.name}` : ""}
        </div>
      </div>

      <div style={{ textAlign: "right" }}>
        <Tooltip title={`SLA ${ticket.slaHours}ч · возраст ${formatHours(ticket.ageHours)}`} arrow>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#B91C1C" }}>
              +{formatHours(ticket.overdueHours)}
            </div>
            <div style={{ fontSize: 10, color: "#94A3B8" }}>
              {moment(ticket.createdAt).fromNow()}
            </div>
          </div>
        </Tooltip>
      </div>
    </Link>
  );
};

export interface TicketsSlaBreachesProps {
  /** Сколько тикетов показывать в списке. По умолчанию 10. */
  limit?: number;
  /** Кастомные SLA в часах. Если не передавать — дефолты бэка (HIGH=8, MEDIUM=24, LOW=72). */
  slaHigh?: number;
  slaMedium?: number;
  slaLow?: number;
}

/**
 * Виджет «SLA-нарушители». Показывает количество просроченных тикетов
 * с разбивкой по приоритету и top-N самых старых нарушителей.
 */
export const TicketsSlaBreaches = ({
  limit = 10,
  slaHigh,
  slaMedium,
  slaLow,
}: TicketsSlaBreachesProps) => {
  const { data, isLoading, error } = useSlaBreaches({ limit, slaHigh, slaMedium, slaLow });

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#FEE2E2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdWarningAmber size={20} color="#B91C1C" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.2 }}>
                <Tooltip title="тикеты, где первый ответ или решение превысили норматив">
                  <span>SLA-нарушители</span>
                </Tooltip>
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: "#B91C1C",
                }}
              >
                {isLoading ? "—" : (data?.totalCount ?? 0)}
              </div>
            </div>
          </div>

          {data && <PriorityBreakdown byPriority={data.byPriority} sla={data.slaHours} />}
        </div>

        {isLoading && !data && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && error && (
          <div style={{ textAlign: "center", color: "#94A3B8", padding: 24 }}>
            Не удалось загрузить SLA-нарушения
          </div>
        )}

        {!isLoading && data && data.totalCount === 0 && (
          <div style={{ textAlign: "center", color: "#94A3B8", padding: 32 }}>
            Все активные тикеты в рамках SLA
          </div>
        )}

        {data && data.items.length > 0 && (
          <div style={{ borderTop: "1px solid #F1F5F9" }}>
            {data.items.map((t) => (
              <BreachRow key={t.id} ticket={t} />
            ))}
          </div>
        )}

        {data && data.totalCount > data.items.length && (
          <div
            style={{
              textAlign: "right",
              fontSize: 11,
              color: "#94A3B8",
              marginTop: 8,
            }}
          >
            Показаны {data.items.length} из {data.totalCount}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketsSlaBreaches;
